import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { entitiesApi, paymentsApi, ecosystemApi, docsApi, publicUrl } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { PageShell } from "./Overview";
import { StatusPill } from "./Overview";
import { ArrowUpRight, ShieldCheck, Wand2, Zap, Globe, Copy, EyeOff, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";

const EntityDetail = () => {
  const { id } = useParams();
  const { currency, format } = useCurrency();
  const [data, setData] = useState(null);
  const [eco, setEco] = useState([]);
  const [busyEco, setBusyEco] = useState("");
  const [busyGen, setBusyGen] = useState("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () =>
    Promise.all([entitiesApi.get(id), ecosystemApi.status()])
      .then(([d, all]) => {
        setData(d);
        setEco((all.find((r) => r.entity_id === id)?.products) || []);
      })
      .catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!data) return <PageShell title="Entity"><div className="text-ink-muted">Loading…</div></PageShell>;
  const { entity, partner, tasks, documents } = data;

  const pay = async () => {
    try {
      const { url } = await paymentsApi.checkout({ entity_id: entity.id, origin_url: window.location.origin, currency });
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment error");
    }
  };

  const togglePublish = async () => {
    setPublishBusy(true);
    try {
      if (entity.is_public) {
        await entitiesApi.unpublish(entity.id);
        toast.success("Profile made private");
      } else {
        const res = await entitiesApi.publish(entity.id);
        const url = res.public_url || publicUrl(res.public_slug);
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Profile published · link copied");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update publish state");
    } finally {
      setPublishBusy(false);
    }
  };

  const copyPublicLink = async () => {
    const url = publicUrl(entity.public_slug);
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleEco = async (p) => {
    setBusyEco(p.id);
    try {
      if (p.status === "active") await ecosystemApi.deactivate(entity.id, p.id);
      else await ecosystemApi.activate(entity.id, p.id);
      toast.success(`${p.name} ${p.status === "active" ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyEco("");
    }
  };

  const generate = async (templateId) => {
    setBusyGen(templateId);
    try {
      const d = await docsApi.generate(entity.id, templateId);
      toast.success(`${d.name} generated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusyGen("");
    }
  };

  return (
    <PageShell title={entity.company_name} subtitle={`${entity.country_name} · ${entity.entity_type_name}`}
      action={<StatusPill status={entity.status} />}
    >
      {entity.status !== "active" && entity.payment_status !== "paid" && (
        <div className="mb-8 border border-artha-amber bg-artha-amber/10 p-6 flex items-center justify-between" data-testid="pay-banner">
          <div>
            <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-artha-amber">Action required</div>
            <div className="h-display text-2xl mt-1">Complete payment to assign your partner</div>
          </div>
          <button onClick={pay} data-testid="resume-pay-button"
            className="bg-artha-amber text-artha-ink px-5 py-3 text-sm hover:bg-artha-cream transition-colors">
            Pay {format(entity.price)}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <Panel title="Company details">
            <Detail k="Registration #" v={entity.registration_number || "—"} />
            <Detail k="Incorporation date" v={entity.incorporation_date ? new Date(entity.incorporation_date).toLocaleDateString() : "—"} />
            <Detail k="Directors" v={entity.directors} />
            <Detail k="Shareholders" v={entity.shareholders} />
            <Detail k="Business" v={entity.business_description || "—"} />
          </Panel>

          <Panel title="Draft documents">
            <p className="text-sm text-ink-muted mb-4">
              Generate Artha-branded PDF drafts of your founding documents, auto-filled with the details above.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { id: "moa", label: "MOA" },
                { id: "aoa", label: "AOA" },
                { id: "shareholder_agreement", label: "Shareholders' Agreement" },
              ].map((t) => (
                <button key={t.id} onClick={() => generate(t.id)} disabled={busyGen === t.id}
                  data-testid={`entity-generate-${t.id}`}
                  className="border border-border-subtle px-4 py-3 text-sm text-left hover:border-artha-amber hover:text-artha-amber transition-colors disabled:opacity-50 flex items-center gap-2">
                  <Wand2 size={14} /> {busyGen === t.id ? "Drafting…" : `Generate ${t.label}`}
                </button>
              ))}
            </div>
          </Panel>

          <Panel title={`Compliance (${tasks.length})`} cta={{ to: "/app/compliance", label: "All tasks" }}>
            {tasks.length === 0 && <div className="text-ink-muted text-sm">Tasks appear automatically once the entity is active.</div>}
            {tasks.slice(0, 8).map((t) => (
              <div key={t.id} className="flex justify-between items-start py-3 border-b border-border-subtle last:border-0">
                <div>
                  <div className="text-sm">{t.title}</div>
                  <div className="text-xs text-ink-muted mt-1 font-mono-accent uppercase tracking-[0.18em]">{t.category} · {t.priority}</div>
                </div>
                <div className="font-mono-accent text-xs text-artha-amber">{new Date(t.due_date).toLocaleDateString()}</div>
              </div>
            ))}
          </Panel>

          <Panel title={`Documents (${documents.length})`} cta={{ to: "/app/documents", label: "Open vault" }}>
            {documents.length === 0 && <div className="text-ink-muted text-sm">No documents yet.</div>}
            {documents.slice(0, 5).map((d) => (
              <div key={d.id} className="flex justify-between items-center py-3 border-b border-border-subtle last:border-0">
                <div className="text-sm">{d.name}</div>
                <div className="text-xs font-mono-accent uppercase tracking-[0.18em] text-ink-muted">{d.type}</div>
              </div>
            ))}
          </Panel>
        </div>

        <div className="lg:col-span-4 space-y-8">
          <Panel title="Public profile">
            <p className="text-xs text-ink-muted leading-relaxed">
              Share a verified, investor-facing snapshot of this entity. Personal data is kept private — only aggregates, compliance score, and ecosystem badges are shown.
            </p>
            {entity.is_public ? (
              <>
                <div className="mt-4 flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.22em] text-artha-amber">
                  <Globe size={12} /> Live at artha.mahakalitribunal.com/company/{entity.public_slug}
                </div>
                <div className="mt-4 flex gap-2">
                  <a href={`/company/${entity.public_slug}`} target="_blank" rel="noreferrer"
                    data-testid="entity-view-public"
                    className="flex-1 text-center inline-flex items-center justify-center gap-2 px-3 py-2 border border-border-subtle hover:border-artha-amber hover:text-artha-amber text-xs font-mono-accent uppercase tracking-[0.22em] transition-colors">
                    <ArrowUpRight size={12} /> View
                  </a>
                  <button onClick={copyPublicLink} data-testid="entity-copy-public-link"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-border-subtle hover:border-artha-amber hover:text-artha-amber text-xs font-mono-accent uppercase tracking-[0.22em] transition-colors">
                    {copied ? <><CheckIcon size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                  </button>
                </div>
                <button onClick={togglePublish} disabled={publishBusy} data-testid="entity-unpublish"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs text-ink-muted hover:text-[#C94F4F] transition-colors disabled:opacity-60">
                  <EyeOff size={12} /> Make private
                </button>
              </>
            ) : (
              <button onClick={togglePublish} disabled={publishBusy} data-testid="entity-publish"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-artha-amber text-artha-ink py-2.5 text-sm hover:bg-artha-cream transition-colors disabled:opacity-60">
                <Globe size={14} /> {publishBusy ? "Publishing…" : "Publish public profile"}
              </button>
            )}
          </Panel>

          <Panel title="Assigned partner">
            {partner ? (
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="h-display text-xl">{partner.name}</div>
                    <div className="text-xs font-mono-accent uppercase tracking-[0.18em] text-ink-muted mt-1">
                      {partner.country} · {partner.city}
                    </div>
                  </div>
                  {partner.verified && <span className="inline-flex items-center gap-1 text-[10px] font-mono-accent uppercase tracking-[0.18em] text-artha-amber"><ShieldCheck size={12} />Verified</span>}
                </div>
                <div className="text-sm text-ink-muted mt-3">{partner.specialization}</div>
                <div className="mt-6 grid grid-cols-3 text-center gap-2 border-t border-border-subtle pt-4">
                  <Mini v={`★ ${partner.rating}`} l="Rating" />
                  <Mini v={partner.completed} l="Files" />
                  <Mini v={`${partner.sla_hours}h`} l="SLA" />
                </div>
              </div>
            ) : (
              <div className="text-ink-muted text-sm">Partner will be assigned after payment.</div>
            )}
          </Panel>

          <Panel title="Ecosystem" cta={{ to: "/app/ecosystem", label: "Manage all" }}>
            <p className="text-xs text-ink-muted mb-4 leading-relaxed">
              Wire this entity into the rest of the Mahakali Tribunal stack.
            </p>
            <div className="space-y-2">
              {eco.slice(0, 4).map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2" data-testid={`entity-eco-${p.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color }} />
                    <div className="min-w-0">
                      <div className="text-sm truncate">{p.name}</div>
                      <div className="text-[10px] font-mono-accent uppercase tracking-[0.2em] text-ink-muted">{p.tagline}</div>
                    </div>
                  </div>
                  <button onClick={() => toggleEco(p)} disabled={busyEco === p.id}
                    data-testid={`entity-eco-toggle-${p.id}`}
                    className={`text-[10px] font-mono-accent uppercase tracking-[0.2em] px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
                      p.status === "active"
                        ? "bg-artha-amber/20 text-artha-amber hover:bg-artha-amber hover:text-artha-ink"
                        : "border border-border-subtle text-ink-muted hover:border-artha-amber hover:text-artha-amber"
                    }`}>
                    {p.status === "active" ? "Active" : <><Zap size={10} className="inline mr-1" />Activate</>}
                  </button>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </PageShell>
  );
};

const Panel = ({ title, cta, children }) => (
  <div className="border border-border-subtle bg-surface-card p-6">
    <div className="flex items-end justify-between mb-4">
      <h2 className="h-display text-xl">{title}</h2>
      {cta && <Link to={cta.to} className="text-xs text-artha-amber hover:text-artha-cream font-mono-accent uppercase tracking-[0.2em]">{cta.label}</Link>}
    </div>
    {children}
  </div>
);

const Detail = ({ k, v }) => (
  <div className="flex justify-between py-3 border-b border-border-subtle last:border-0">
    <span className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted">{k}</span>
    <span className="text-sm text-ink-body text-right max-w-[60%]">{v}</span>
  </div>
);

const Mini = ({ v, l }) => (
  <div>
    <div className="font-mono-accent text-sm">{v}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-mono-accent text-ink-muted mt-1">{l}</div>
  </div>
);

export default EntityDetail;

